import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  DatePicker,
  Spin,
  Tooltip,
} from 'antd';
import {
  PlusCircleOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  TeamOutlined,
  CheckCircleFilled,
  CheckSquareOutlined,
  UsergroupAddOutlined,
  TrophyOutlined,
  ExclamationCircleOutlined,
  FolderOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CheckSquareFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import locale from 'antd/es/date-picker/locale/fr_FR';

import Main from '../../components/layouts/Main';
import Breadcrumb from '../../components/utils/Breadcrumb';
import TacheBacklogItem from '../../components/tache/TacheBacklogItem';
import AffaireBacklogItem from '../../components/affaire/AffaireBacklogItem';
import TacheQuickView from '../../components/tache/TacheQuickView';
import AffaireQuickView from '../../components/affaire/AffaireQuickView';
import TacheCreateForm from '../../components/tache/TacheCreateForm';
import AffaireCreateForm from '../../components/affaire/AffaireCreateForm';
import { useDetailPreference } from '../../hooks/useDetailPreference';
import { useUserContext } from '../../contexts/UserContext';
import { DashboardService } from '../../services/DashboardService';
import { TacheService } from '../../services/TacheService';
import { AffaireService } from '../../services/AffaireService';
import { catchError } from '../../services/DaoService';

import styles from './TableauDeBordPage.module.scss';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { RangePicker } = DatePicker;

const dashSvc = DashboardService.getInstance();
const tacheSvc = TacheService.getInstance();
const affaireSvc = AffaireService.getInstance();

const PersonnalDashboardPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useUserContext();
  const { mode } = useDetailPreference();

  // ── Period filter state — année civile en cours (1er janvier → aujourd'hui) ──
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('year'),
    dayjs(),
  ]);

  // ── KPI numbers ──
  const [numbers, setNumbers] = useState(null);
  const [loadingNumbers, setLoadingNumbers] = useState(false);

  // ── Overdue taches ──
  const [overdueTaches, setOverdueTaches] = useState([]);
  const [loadingOverdue, setLoadingOverdue] = useState(false);

  // ── Active affaires ──
  const [activeAffaires, setActiveAffaires] = useState([]);
  const [loadingAffaires, setLoadingAffaires] = useState(false);

  // ── Latest taches ──
  const [latestTaches, setLatestTaches] = useState([]);
  const [loadingLatest, setLoadingLatest] = useState(false);

  // ── Task breakdown ──
  const [breakdown, setBreakdown] = useState({ overdue: 0, inProgress: 0 });
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  // ── Assigned by me ──
  const [assignedByMe, setAssignedByMe] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);

  // ── Feed ──
  const [feed, setFeed] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);

  // ── Quick view state ──
  const [selectedTacheUid, setSelectedTacheUid] = useState(null);
  const [selectedAffaireUid, setSelectedAffaireUid] = useState(null);

  // ── Create form modals ──
  const [showTacheCreate, setShowTacheCreate] = useState(false);
  const [showAffaireCreate, setShowAffaireCreate] = useState(false);

  // ── Load KPI numbers ──
  const loadNumbers = useCallback(() => {
    if (!dateRange || dateRange.length < 2) return;
    setLoadingNumbers(true);
    const data = {
      startDate: dateRange[0].format('YYYY-MM-DD'),
      endDate: dateRange[1].format('YYYY-MM-DD'),
    };
    dashSvc
      .getMyNumbers(data)
      .then((res) => setNumbers(res.data))
      .catch(catchError)
      .finally(() => setLoadingNumbers(false));
  }, [dateRange]);

  // ── Load overdue taches ──
  const loadOverdueTaches = useCallback(() => {
    if (!currentUser?.id) return;
    setLoadingOverdue(true);
    tacheSvc
      .search({
        responsable_id: currentUser.id,
        resolu: false,
        date_to: dayjs().format('YYYY-MM-DD'),
      })
      .then((res) => setOverdueTaches((res.data?.data || res.data || []).slice(0, 5)))
      .catch(catchError)
      .finally(() => setLoadingOverdue(false));
  }, [currentUser?.id]);

  // ── Load active affaires ──
  const loadActiveAffaires = useCallback(() => {
    if (!currentUser?.id) return;
    setLoadingAffaires(true);
    affaireSvc
      .search({ responsable_id: currentUser.id, statut: 'pending' })
      .then((res) => setActiveAffaires((res.data?.data || res.data || []).slice(0, 5)))
      .catch(catchError)
      .finally(() => setLoadingAffaires(false));
  }, [currentUser?.id]);

  // ── Load latest taches ──
  const loadLatestTaches = useCallback(() => {
    setLoadingLatest(true);
    dashSvc
      .getMyLatestTaches()
      .then((res) => setLatestTaches((res.data || []).slice(0, 8)))
      .catch(catchError)
      .finally(() => setLoadingLatest(false));
  }, []);

  // ── Load task breakdown ──
  const loadBreakdown = useCallback(() => {
    if (!currentUser?.id) return;
    setLoadingBreakdown(true);
    Promise.all([
      tacheSvc.search({ responsable_id: currentUser.id, resolu: false, date_to: dayjs().format('YYYY-MM-DD') }),
      tacheSvc.search({ responsable_id: currentUser.id, resolu: false }),
    ])
      .then(([overdueRes, inProgressRes]) => {
        const overdueCount = overdueRes.data?.total ?? (overdueRes.data?.data || overdueRes.data || []).length;
        const inProgressCount = inProgressRes.data?.total ?? (inProgressRes.data?.data || inProgressRes.data || []).length;
        setBreakdown({ overdue: overdueCount, inProgress: inProgressCount });
      })
      .catch(catchError)
      .finally(() => setLoadingBreakdown(false));
  }, [currentUser?.id]);

  // ── Load assigned by me ──
  const loadAssignedByMe = useCallback(() => {
    setLoadingAssigned(true);
    dashSvc
      .getAssignedByMe({})
      .then((res) => setAssignedByMe((res.data || []).slice(0, 5)))
      .catch(() => {
        // Fallback to latest taches if endpoint not available
        dashSvc
          .getMyLatestTaches()
          .then((res) => setAssignedByMe((res.data || []).slice(0, 5)))
          .catch(catchError);
      })
      .finally(() => setLoadingAssigned(false));
  }, []);

  // ── Load feed ──
  const loadFeed = useCallback(() => {
    setLoadingFeed(true);
    dashSvc
      .getFeed(1, {})
      .then((res) => setFeed((res.data?.data || res.data || []).slice(0, 8)))
      .catch(catchError)
      .finally(() => setLoadingFeed(false));
  }, []);

  useEffect(() => { loadNumbers(); }, [loadNumbers]);
  useEffect(() => { loadOverdueTaches(); }, [loadOverdueTaches]);
  useEffect(() => { loadActiveAffaires(); }, [loadActiveAffaires]);
  useEffect(() => { loadLatestTaches(); }, [loadLatestTaches]);
  useEffect(() => { loadBreakdown(); }, [loadBreakdown]);
  useEffect(() => { loadAssignedByMe(); }, [loadAssignedByMe]);
  useEffect(() => { loadFeed(); }, [loadFeed]);

  const formattedDate = dayjs().locale('fr').format('dddd D MMMM YYYY');
  const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

  const kpiCards = [
    {
      icon: <FolderOpenOutlined />,
      color: 'var(--primary-color, #3E65AC)',
      number: numbers?.nombreDossierResponsable ?? '—',
      label: 'Dossiers responsable',
      onClick: () => navigate('/affaire'),
    },
    {
      icon: <TeamOutlined />,
      color: '#7c3aed',
      number: numbers?.nombreDossierImplique ?? '—',
      label: 'Dossiers collaborateur',
      onClick: () => navigate('/affaire'),
    },
    {
      icon: <CheckCircleFilled />,
      color: '#16a34a',
      number: numbers?.nombreMyDossierImpliqueTermine ?? '—',
      label: 'Dossiers terminés',
      onClick: () => navigate('/affaire'),
    },
    {
      icon: <CheckSquareOutlined />,
      color: '#ea580c',
      number: numbers?.nombreTacheAffectee ?? '—',
      label: 'Tâches affectées',
      onClick: () => navigate('/tache'),
    },
    {
      icon: <UsergroupAddOutlined />,
      color: '#4338ca',
      number: numbers?.nombreTacheImpliquee ?? '—',
      label: 'Tâches collaborateur',
      onClick: () => navigate('/tache'),
    },
    {
      icon: <TrophyOutlined />,
      color: '#0d9488',
      number: numbers?.nombreMyTacheTerminee ?? '—',
      label: 'Tâches terminées',
      onClick: () => navigate('/tache'),
    },
  ];

  const handleTacheUpdate = (updated) => {
    if (!updated) return;
    if (updated.statut?.resolu) {
      setOverdueTaches(prev => prev.filter(t => t.uid !== updated.uid));
      setLatestTaches(prev => prev.filter(t => t.uid !== updated.uid));
      setAssignedByMe(prev => prev.filter(t => t.uid !== updated.uid));
      setFeed(prev => prev.filter(e => e.item?.uid !== updated.uid));
    } else {
      const updater = prev => prev.map(t => t.uid === updated.uid ? updated : t);
      setOverdueTaches(updater);
      setLatestTaches(updater);
      setAssignedByMe(updater);
      setFeed(prev => prev.map(e => e.item?.uid === updated.uid ? { ...e, item: updated } : e));
    }
  };

  const handleAffaireUpdate = (updated) => {
    if (!updated) return;
    if (updated.done) {
      setActiveAffaires(prev => prev.filter(a => a.uid !== updated.uid));
      setFeed(prev => prev.filter(e => e.item?.uid !== updated.uid));
    } else {
      setActiveAffaires(prev => prev.map(a => a.uid === updated.uid ? updated : a));
      setFeed(prev => prev.map(e => e.item?.uid === updated.uid ? { ...e, item: updated } : e));
    }
  };

  return (
    <Main>
      <Breadcrumb
        parentLink="/"
        canSee={true}
        parentLinkTitle="Accueil"
        title="Mon tableau de bord"
      />

      <div className={styles.page}>
        {/* ── A. Header ── */}
        <div className={styles.header}>
          <div className={styles.greeting}>
            <Avatar
              size={48}
              src={currentUser?.avatar}
              icon={!currentUser?.avatar ? <UserOutlined /> : undefined}
              style={{ background: 'var(--primary-color, #3E65AC)', flexShrink: 0 }}
            />
            <div className={styles.greetingText}>
              <h2>Bonjour, {currentUser?.name ?? 'vous'} 👋</h2>
              <p>{capitalizedDate}</p>
            </div>
          </div>

          <div className={styles.headerRight}>
            <RangePicker
              locale={locale}
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
              }}
              format="DD/MM/YYYY"
              size="small"
            />
            <div className={styles.headerActions}>
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                size="small"
                onClick={() => setShowTacheCreate(true)}
              >
                Nouvelle tâche
              </Button>
              <Button
                icon={<FolderAddOutlined />}
                size="small"
                onClick={() => setShowAffaireCreate(true)}
              >
                Nouveau dossier
              </Button>
            </div>
          </div>
        </div>

        {/* ── B. KPI Strip ── */}
        <div className={styles.kpiStrip}>
          {loadingNumbers ? (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 24 }}>
              <Spin />
            </div>
          ) : (
            kpiCards.map((card, idx) => (
              <Tooltip key={idx} title={card.label}>
                <div className={styles.kpiCard} onClick={card.onClick} role="button" tabIndex={0}>
                  <span className={styles.kpiIcon} style={{ color: card.color }}>
                    {card.icon}
                  </span>
                  <div className={styles.kpiNumber}>{card.number}</div>
                  <div className={styles.kpiLabel}>{card.label}</div>
                </div>
              </Tooltip>
            ))
          )}
        </div>

        {/* ── C. Main Grid ── */}
        <div className={styles.mainGrid}>
          {/* LEFT — Mes priorités */}
          <div>
            {/* Panel 1: Tâches en retard */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>
                  <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                  Tâches en retard ou urgentes
                </span>
                <span className={styles.panelBadge}>{overdueTaches.length}</span>
              </div>
              <div className={styles.panelBody}>
                {loadingOverdue ? (
                  <div className={styles.emptyPanel}><Spin /></div>
                ) : overdueTaches.length === 0 ? (
                  <div className={styles.emptyPanel}>Aucune tâche en retard 🎉</div>
                ) : (
                  overdueTaches.map((tache) => (
                    <TacheBacklogItem
                      key={tache.id || tache.uid}
                      tache={tache}
                      showAffaire={true}
                      onItemClick={setSelectedTacheUid}
                      onUpdate={handleTacheUpdate}
                    />
                  ))
                )}
              </div>
              <div className={styles.panelFooter}>
                <a onClick={() => navigate('/tache')} style={{ cursor: 'pointer' }}>Voir toutes mes tâches →</a>
              </div>
            </div>

            {/* Panel 2: Dossiers actifs */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>
                  <FolderOutlined style={{ color: 'var(--primary-color, #3E65AC)' }} />
                  Dossiers actifs
                </span>
                <span className={styles.panelBadge}>{activeAffaires.length}</span>
              </div>
              <div className={styles.panelBody}>
                {loadingAffaires ? (
                  <div className={styles.emptyPanel}><Spin /></div>
                ) : activeAffaires.length === 0 ? (
                  <div className={styles.emptyPanel}>Aucun dossier actif</div>
                ) : (
                  activeAffaires.map((affaire) => (
                    <AffaireBacklogItem
                      key={affaire.id || affaire.uid}
                      affaire={affaire}
                      onItemClick={setSelectedAffaireUid}
                      onUpdate={handleAffaireUpdate}
                    />
                  ))
                )}
              </div>
              <div className={styles.panelFooter}>
                <a onClick={() => navigate('/affaire')} style={{ cursor: 'pointer' }}>Voir tous mes dossiers →</a>
              </div>
            </div>

          </div>

          {/* RIGHT — Vue d'ensemble */}
          <div>
            {/* Widget 1: Répartition des tâches */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>
                  <CheckSquareOutlined style={{ color: '#ea580c' }} />
                  Répartition des tâches
                </span>
              </div>
              {loadingBreakdown ? (
                <div className={styles.emptyPanel}><Spin /></div>
              ) : (
                <div className={styles.statPills}>
                  <div className={styles.statPill}>
                    <span className={styles.statPillNum} style={{ color: '#cf1322' }}>
                      {breakdown.overdue}
                    </span>
                    <span className={styles.statPillLabel}>En retard</span>
                  </div>
                  <div className={styles.statPill}>
                    <span className={styles.statPillNum} style={{ color: '#ea580c' }}>
                      {breakdown.inProgress}
                    </span>
                    <span className={styles.statPillLabel}>En cours</span>
                  </div>
                  <div className={styles.statPill}>
                    <span className={styles.statPillNum} style={{ color: '#16a34a' }}>
                      {numbers?.nombreMyTacheTerminee ?? '—'}
                    </span>
                    <span className={styles.statPillLabel}>Terminées</span>
                  </div>
                </div>
              )}
            </div>

            {/* Widget 2: Ce que j'ai confié */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>
                  <UsergroupAddOutlined style={{ color: '#4338ca' }} />
                  Ce que j&apos;ai confié
                </span>
                <span className={styles.panelBadge}>{assignedByMe.length}</span>
              </div>
              <div className={styles.panelBody}>
                {loadingAssigned ? (
                  <div className={styles.emptyPanel}><Spin /></div>
                ) : assignedByMe.length === 0 ? (
                  <div className={styles.emptyPanel}>Aucune tâche confiée</div>
                ) : (
                  assignedByMe.map((tache) => (
                    <div
                      key={tache.id || tache.uid}
                      className={styles.assignedItem}
                      onClick={() => setSelectedTacheUid(tache.uid)}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                    >
                      <Avatar
                        size={24}
                        src={tache.responsable?.avatar}
                        icon={!tache.responsable?.avatar ? <UserOutlined /> : undefined}
                        style={{ background: 'var(--primary-color, #3E65AC)', flexShrink: 0 }}
                      />
                      <span className={styles.assignedName}>
                        {tache.nom || tache.titre || tache.name}
                      </span>
                      {tache.date_limite || tache.deadline ? (
                        <span className={styles.deadlineBadge}>
                          {dayjs(tache.date_limite || tache.deadline).format('DD/MM')}
                        </span>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Widget 3: Activité récente */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>
                  <ClockCircleOutlined style={{ color: '#0d9488' }} />
                  Activité récente
                </span>
              </div>
              <div className={styles.panelBody}>
                {loadingFeed ? (
                  <div className={styles.emptyPanel}><Spin /></div>
                ) : feed.length === 0 ? (
                  <div className={styles.emptyPanel}>Aucune activité récente</div>
                ) : (
                  <ul className={styles.feedList}>
                    {feed.map((entry, idx) => {
                      const isAffaire = entry.type === 'affaire';
                      const data = entry.item;
                      if (!data) return null;
                      const avatar = isAffaire
                        ? data.responsable?.avatar
                        : data.responsable?.avatar_link ?? data.user?.avatar;
                      const avatarInitial = isAffaire
                        ? data.responsable?.name?.[0]
                        : data.responsable?.name?.[0] ?? data.user?.name?.[0];
                      const typeColor = 'var(--primary-color, #3E65AC)';
                      const typeIcon = isAffaire
                        ? <FolderOpenOutlined style={{ fontSize: 11 }} />
                        : <CheckSquareFilled style={{ fontSize: 11 }} />;
                      const uid = data.uid;
                      return (
                        <li
                          key={uid || idx}
                          className={styles.feedItem}
                          onClick={() => isAffaire ? setSelectedAffaireUid(uid) : setSelectedTacheUid(uid)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Avatar
                            className={styles.feedAvatar}
                            size={28}
                            src={avatar}
                            style={{ background: typeColor, flexShrink: 0 }}
                          >
                            {!avatar && (avatarInitial ?? <UserOutlined />)}
                          </Avatar>
                          <span className={styles.feedContent}>
                            <span className={styles.feedTypeChip} style={{ background: 'rgba(62,101,172,0.10)', color: typeColor }}>
                              {typeIcon} {isAffaire ? 'Dossier' : 'Tâche'}
                            </span>
                            <span className={styles.feedText}>{data.nom}</span>
                          </span>
                          <span className={styles.feedTime}>
                            {entry.updated_at ? dayjs(entry.updated_at).fromNow() : ''}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── D. Quick Views ── */}
      {selectedTacheUid && (
        <TacheQuickView
          uid={selectedTacheUid}
          mode={mode}
          onClose={() => setSelectedTacheUid(null)}
          onUpdate={handleTacheUpdate}
        />
      )}
      {selectedAffaireUid && (
        <AffaireQuickView
          uid={selectedAffaireUid}
          mode={mode}
          onClose={() => setSelectedAffaireUid(null)}
          onUpdate={handleAffaireUpdate}
        />
      )}

      {/* ── Create Forms ── */}
      <TacheCreateForm
        visible={showTacheCreate}
        onCancel={() => setShowTacheCreate(false)}
        onCreate={(data) => {
          setShowTacheCreate(false);
          setSelectedTacheUid(data?.uid);
          loadLatestTaches();
          loadNumbers();
        }}
      />
      <AffaireCreateForm
        visible={showAffaireCreate}
        onCancel={() => setShowAffaireCreate(false)}
        onCreate={() => {
          setShowAffaireCreate(false);
          loadActiveAffaires();
          loadNumbers();
        }}
      />
    </Main>
  );
};

export default PersonnalDashboardPage;
